import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPlus, FiSearch, FiChevronDown, FiChevronRight,
  FiArrowDownCircle, FiArrowUpCircle, FiX, FiTrash2, FiShoppingBag, FiSlash,
} from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtFecha, fmtCant, localDate } from '../utils/format';
import '../styles/Inventarios.css';

const hoy = () => localDate();
const fmtMonto = (n) => parseFloat(n || 0).toFixed(2);

/* ── Modal nuevo movimiento ────────────────────────────────────── */
function ModalMovimiento({ almacenes, onClose, onSaved }) {
  const EMPTY = { almacen_id: '', tipo: 'ENTRADA', causa_id: '', fecha: hoy(), observacion: '' };
  const [form,    setForm]    = useState(EMPTY);
  const [causas,  setCausas]  = useState([]);
  const [lineas,  setLineas]  = useState([]);
  const [busq,    setBusq]    = useState('');
  const [resultados, setRes]  = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    api.get('/api/movimientos/causas', { params: { tipo: form.tipo } })
      .then(r => { setCausas(r.data?.data ?? []); setForm(p => ({ ...p, causa_id: r.data?.data?.[0]?.id || '' })); })
      .catch(() => {});
  }, [form.tipo]);

  const buscarProducto = useCallback(async (q) => {
    if (!q.trim()) { setRes([]); return; }
    try {
      const r = await api.get('/api/productos', { params: { q } });
      setRes((r.data?.data || []).slice(0, 8));
    } catch { setRes([]); }
  }, []);

  const busqTimer = useRef(null);
  const handleBusq = (v) => {
    setBusq(v);
    clearTimeout(busqTimer.current);
    busqTimer.current = setTimeout(() => buscarProducto(v), 250);
  };

  const agregarProducto = (p) => {
    if (lineas.find(l => l.producto_id === p.id)) { setBusq(''); setRes([]); return; }
    setLineas(prev => [...prev, {
      producto_id: p.id, producto_nombre: p.nombre, codigo: p.codigo,
      unidad_short: p.unidad_minima_short || p.unidad_minima_nombre || 'UND',
      cantidad: '',
    }]);
    setBusq(''); setRes([]);
    searchRef.current?.focus();
  };

  const setLinea = (idx, field, val) => setLineas(p => p.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  const quitarLinea = (idx) => setLineas(p => p.filter((_, i) => i !== idx));

  const guardar = async () => {
    setErr('');
    for (const l of lineas) {
      if (!l.cantidad || parseFloat(l.cantidad) <= 0) {
        setErr(`Cantidad inválida para "${l.producto_nombre}"`); return;
      }
    }
    setSaving(true);
    try {
      await api.post('/api/movimientos', {
        ...form,
        lineas: lineas.map(l => ({ producto_id: l.producto_id, cantidad: parseFloat(l.cantidad) })),
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="inv-modal-overlay">
      <div className="inv-modal inv-modal-lg">
        <div className="inv-modal-header">
          <span>Nuevo Movimiento de Inventario</span>
          <button className="inv-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="inv-modal-body">
          <div className="inv-form-grid">
            <div className="inv-field">
              <label>Almacén *</label>
              <select className="inv-select" value={form.almacen_id}
                onChange={e => setForm(p => ({ ...p, almacen_id: e.target.value }))}>
                <option value="">— seleccione —</option>
                {almacenes.filter(a=>a.activo).map(a => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <div className="inv-field">
              <label>Tipo *</label>
              <div className="inv-tipo-btns">
                {['ENTRADA','SALIDA'].map(t => (
                  <button key={t}
                    className={`inv-tipo-btn ${form.tipo === t ? (t==='ENTRADA'?'entrada':'salida') : ''}`}
                    onClick={() => setForm(p => ({ ...p, tipo: t }))}>
                    {t === 'ENTRADA' ? <FiArrowDownCircle size={13}/> : <FiArrowUpCircle size={13}/>}
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="inv-field">
              <label>Causa *</label>
              <select className="inv-select" value={form.causa_id}
                onChange={e => setForm(p => ({ ...p, causa_id: e.target.value }))}>
                <option value="">— seleccione —</option>
                {causas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="inv-field">
              <label>Fecha *</label>
              <input type="date" className="inv-input" value={form.fecha}
                onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} />
            </div>
            <div className="inv-field inv-field-full">
              <label>Observación</label>
              <input className="inv-input" value={form.observacion}
                onChange={e => setForm(p => ({ ...p, observacion: e.target.value }))}
                placeholder="Opcional" />
            </div>
          </div>

          <div className="inv-lineas-header">
            <span>Productos</span>
            <div className="inv-search-prod" style={{ position: 'relative' }}>
              <FiSearch size={12} />
              <input
                ref={searchRef}
                placeholder="Buscar producto para agregar..."
                value={busq}
                onChange={e => handleBusq(e.target.value)}
                autoComplete="off"
              />
              {resultados.length > 0 && (
                <ul className="inv-prod-dropdown">
                  {resultados.map(p => (
                    <li key={p.id} onClick={() => agregarProducto(p)}>
                      <span className="inv-pd-nombre">{p.nombre}</span>
                      <span className="inv-pd-cod">{p.codigo || ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <table className="inv-lineas-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Unidad</th>
                <th className="inv-th-cant">Cantidad</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lineas.length === 0 ? (
                <tr><td colSpan={4} className="inv-lineas-empty">
                  Busque y agregue productos arriba
                </td></tr>
              ) : lineas.map((l, i) => (
                <tr key={l.producto_id}>
                  <td>
                    <span className="inv-linea-nombre">{l.producto_nombre}</span>
                    {l.codigo && <span className="inv-linea-cod">{l.codigo}</span>}
                  </td>
                  <td>{l.unidad_short}</td>
                  <td>
                    <input
                      type="number" min="0.01" step="0.01"
                      className="inv-input-cant"
                      value={l.cantidad}
                      onChange={e => setLinea(i, 'cantidad', e.target.value)}
                      autoFocus={i === lineas.length - 1}
                    />
                  </td>
                  <td>
                    <button className="ia-btn ia-btn--danger" onClick={() => quitarLinea(i)} title="Quitar">
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {err && <div className="inv-modal-error">{err}</div>}
        <div className="inv-modal-footer">
          <button className="inv-btn-save" onClick={guardar}
            disabled={saving || !form.almacen_id || !form.causa_id || lineas.length === 0}>
            {saving ? 'Guardando...' : 'Registrar Movimiento'}
          </button>
          <button className="inv-btn-cancel" onClick={onClose}>× Salir</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal compras pendientes de ingreso ───────────────────────── */
function ModalComprasPendientes({ almacenes, onClose, onSaved }) {
  const { user } = useAuth();
  const esAdmin = !user ||
    user.rol?.toLowerCase() === 'admin' ||
    user.role?.toLowerCase() === 'admin';

  const [compras,      setCompras]      = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [error,        setError]        = useState('');
  const [abiertos,     setAbiertos]     = useState({});
  const [compraActual, setCompraActual] = useState(null);
  const [formIngreso,  setFormIngreso]  = useState(null);
  const [guardando,    setGuardando]    = useState(false);
  const [errGuardar,   setErrGuardar]   = useState('');

  useEffect(() => {
    api.get('/api/compras/pendientes-almacen')
      .then(r => setCompras(r.data?.data || []))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setCargando(false));
  }, []);

  const fmtDocumento = (c) => {
    if (!c.nro_documento && !c.serie) return '—';
    if (c.tipo_documento === 'NINGUNO') return c.nro_documento || '—';
    const partes = [c.tipo_documento];
    if (c.serie) partes.push(c.serie);
    if (c.nro_documento) partes.push(c.nro_documento);
    return partes.join(' ');
  };

  const toggleAbierto = (id) => setAbiertos(p => ({ ...p, [id]: !p[id] }));

  const iniciarIngreso = (compra) => {
    const lineasConProducto = compra.detalle.filter(d => d.producto_id);
    const doc = fmtDocumento(compra);
    setCompraActual(compra);
    setFormIngreso({
      almacen_id:  almacenes.filter(a => a.activo)[0]?.id || '',
      fecha:       hoy(),
      observacion: `Ingreso por compra ${compra.numero}${doc && doc !== '—' ? ` - ${doc}` : ''}`,
      lineas: lineasConProducto.map(d => ({
        producto_id:     d.producto_id,
        producto_nombre: d.producto_nombre,
        unidad:          d.unidad_compra || '—',
        precio_unitario: d.precio_unitario,
        cantidad:        parseFloat(d.cantidad),
      })),
    });
    setErrGuardar('');
  };

  const cancelarIngreso = () => {
    setCompraActual(null);
    setFormIngreso(null);
    setErrGuardar('');
  };

  const setLinea = (idx, val) => setFormIngreso(p => ({
    ...p,
    lineas: p.lineas.map((l, i) => i === idx ? { ...l, cantidad: val } : l),
  }));

  const confirmar = async () => {
    setErrGuardar('');
    if (!formIngreso.almacen_id) { setErrGuardar('Seleccione un almacén'); return; }
    for (const l of formIngreso.lineas) {
      if (!l.cantidad || parseFloat(l.cantidad) <= 0) {
        setErrGuardar(`Cantidad inválida para "${l.producto_nombre}"`); return;
      }
    }
    setGuardando(true);
    try {
      await api.post(`/api/compras/${compraActual.id}/ingresar`, {
        almacen_id:  formIngreso.almacen_id,
        fecha:       formIngreso.fecha,
        observacion: formIngreso.observacion,
        lineas: formIngreso.lineas.map(l => ({
          producto_id: l.producto_id,
          cantidad:    parseFloat(l.cantidad),
        })),
      });
      setCompras(prev => prev.filter(c => c.id !== compraActual.id));
      setCompraActual(null);
      setFormIngreso(null);
      onSaved();
    } catch (e) {
      setErrGuardar(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="inv-modal-overlay">
      <div className="inv-modal inv-modal-xl">
        <div className="inv-modal-header">
          {formIngreso ? (
            <span>Ingresar al Almacén — <strong>{compraActual.numero}</strong></span>
          ) : (
            <span>Compras Pendientes de Ingreso</span>
          )}
          <button className="inv-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="inv-modal-body">
          {formIngreso ? (
            /* ─── Vista: formulario de ingreso ─── */
            <>
              <div className="cpi-ingreso-info">
                <span><strong>Proveedor:</strong> {compraActual.proveedor_nombre || '—'}</span>
                <span><strong>Fecha compra:</strong> {fmtFecha(compraActual.fecha)}</span>
                <span><strong>Documento:</strong> {fmtDocumento(compraActual)}</span>
              </div>

              <div className="inv-form-grid">
                <div className="inv-field">
                  <label>Almacén destino *</label>
                  <select className="inv-select" value={formIngreso.almacen_id}
                    onChange={e => setFormIngreso(p => ({ ...p, almacen_id: e.target.value }))}>
                    <option value="">— seleccione —</option>
                    {almacenes.filter(a => a.activo).map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="inv-field">
                  <label>Fecha ingreso *</label>
                  <input type="date" className="inv-input" value={formIngreso.fecha}
                    onChange={e => setFormIngreso(p => ({ ...p, fecha: e.target.value }))} />
                </div>
                <div className="inv-field inv-field-full">
                  <label>Observación</label>
                  <input className="inv-input" value={formIngreso.observacion}
                    onChange={e => setFormIngreso(p => ({ ...p, observacion: e.target.value }))} />
                </div>
              </div>

              <table className="inv-lineas-table cpi-ingreso-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="cpi-th-und">Unidad</th>
                    <th className="cpi-th-cant">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {formIngreso.lineas.length === 0 ? (
                    <tr><td colSpan={3} className="inv-lineas-empty">
                      Esta compra no tiene productos con código registrado
                    </td></tr>
                  ) : formIngreso.lineas.map((l, i) => (
                    <tr key={l.producto_id}>
                      <td><span className="inv-linea-nombre">{l.producto_nombre}</span></td>
                      <td>{l.unidad}</td>
                      <td className="cpi-td-cant">{l.cantidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            /* ─── Vista: lista de compras pendientes ─── */
            cargando ? (
              <div className="inv-loading">Cargando compras pendientes...</div>
            ) : error ? (
              <div className="inv-error">{error}</div>
            ) : compras.length === 0 ? (
              <div className="cpi-empty">
                <FiShoppingBag size={32} />
                <span>No hay compras pendientes de ingreso</span>
              </div>
            ) : (
              <div className="cpi-lista">
                {compras.map(c => {
                  const lineasConProd = c.detalle.filter(d => d.producto_id);
                  const abierto = !!abiertos[c.id];
                  return (
                    <div key={c.id} className="cpi-card">
                      <div className="cpi-card-header" onClick={() => toggleAbierto(c.id)}>
                        <span className="cpi-chevron">
                          {abierto ? <FiChevronDown size={13}/> : <FiChevronRight size={13}/>}
                        </span>
                        <span className="cpi-num">{c.numero}</span>
                        <span className="cpi-prov">{c.proveedor_nombre || '—'}</span>
                        <span className="cpi-fecha">{fmtFecha(c.fecha)}</span>
                        <span className="cpi-doc">{fmtDocumento(c)}</span>
                        <button
                          className="cpi-btn-ingresar"
                          disabled={lineasConProd.length === 0}
                          title={lineasConProd.length === 0
                            ? 'Sin productos con código registrado'
                            : 'Ingresar al almacén'}
                          onClick={e => { e.stopPropagation(); iniciarIngreso(c); }}
                        >
                          Ingresar →
                        </button>
                      </div>

                      {abierto && (
                        <div className="cpi-card-body">
                          <table className="cpi-detalle-table">
                            <thead>
                              <tr>
                                <th>Producto</th>
                                <th>Unidad</th>
                                <th>Cantidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.detalle.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="inv-lineas-empty">
                                    Sin detalle
                                  </td>
                                </tr>
                              ) : c.detalle.map(d => (
                                <tr key={d.id} className={!d.producto_id ? 'cpi-row-libre' : ''}>
                                  <td>
                                    {d.producto_nombre}
                                    {!d.producto_id && (
                                      <span className="cpi-badge-libre">texto libre</span>
                                    )}
                                  </td>
                                  <td>{d.unidad_compra || '—'}</td>
                                  <td>{fmtCant(d.cantidad)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {errGuardar && <div className="inv-modal-error">{errGuardar}</div>}
        <div className="inv-modal-footer">
          {formIngreso ? (
            <>
              <button className="inv-btn-save"
                onClick={confirmar}
                disabled={guardando || !formIngreso.almacen_id || formIngreso.lineas.length === 0}>
                {guardando ? 'Procesando...' : '✓ Confirmar Ingreso'}
              </button>
              <button className="inv-btn-cancel" onClick={cancelarIngreso}>← Volver</button>
            </>
          ) : (
            <button className="inv-btn-cancel" onClick={onClose}>× Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Fila expandible ───────────────────────────────────────────── */
function FilaMovimiento({ mov, esAdmin, onAnular }) {
  const [open,       setOpen]       = useState(false);
  const [detalle,    setDetalle]    = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [anulando,   setAnulando]   = useState(false);

  const toggleOpen = async () => {
    if (!open && !detalle) {
      try {
        const r = await api.get(`/api/movimientos/${mov.id}`);
        setDetalle(r.data);
      } catch { setDetalle({ lineas: [] }); }
    }
    setOpen(!open);
  };

  const handleAnular = async (e) => {
    e.stopPropagation();
    if (!confirming) { setConfirming(true); return; }
    setAnulando(true);
    try {
      await api.patch(`/api/movimientos/${mov.id}/anular`);
      onAnular();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al anular');
    } finally {
      setAnulando(false);
      setConfirming(false);
    }
  };

  const anulado = mov.estado === 'ANULADO';

  return (
    <>
      <tr className={`inv-row inv-row-${mov.tipo.toLowerCase()}${anulado ? ' inv-row-anulado' : ''}`}
          onClick={toggleOpen} style={{ cursor: 'pointer' }}>
        <td className="inv-td inv-td-icon">
          {open ? <FiChevronDown size={13}/> : <FiChevronRight size={13}/>}
        </td>
        <td className="inv-td">{mov.almacen_nombre}</td>
        <td className="inv-td inv-td-fecha">{fmtFecha(mov.fecha)}</td>
        <td className="inv-td inv-td-num">{mov.numero}</td>
        <td className="inv-td">
          {anulado
            ? <span className="inv-badge-anulado">ANULADO</span>
            : <span className={`inv-tipo-badge ${mov.tipo === 'ENTRADA' ? 'entrada' : 'salida'}`}>
                {mov.tipo === 'ENTRADA' ? <FiArrowDownCircle size={11}/> : <FiArrowUpCircle size={11}/>}
                {mov.tipo}
              </span>
          }
        </td>
        <td className="inv-td">{mov.causa_nombre || '—'}</td>
        <td className="inv-td inv-td-items">{mov.nro_items}</td>
        <td className="inv-td inv-td-obs">{mov.observacion || '—'}</td>
        <td className="inv-td inv-td-acc" onClick={e => e.stopPropagation()}>
          {esAdmin && !anulado && !mov.venta_id && (
            confirming
              ? <>
                  <button className="inv-btn-anular-confirm" onClick={handleAnular} disabled={anulando}>
                    {anulando ? '...' : '¿Confirmar?'}
                  </button>
                  <button className="inv-btn-anular-cancel" onClick={e => { e.stopPropagation(); setConfirming(false); }}>
                    No
                  </button>
                </>
              : <button className="inv-btn-anular" onClick={handleAnular} title="Anular movimiento">
                  <FiSlash size={12}/>
                </button>
          )}
        </td>
      </tr>
      {open && detalle && (
        <tr className="inv-row-detail">
          <td colSpan={9}>
            <table className="inv-detail-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Código</th>
                  <th>Unidad</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {detalle.lineas.map(l => (
                  <tr key={l.id}>
                    <td>{l.producto_nombre}</td>
                    <td className="inv-td-cod">{l.codigo || '—'}</td>
                    <td>{l.unidad_short || l.unidad_nombre || '—'}</td>
                    <td className="inv-td-cant">{fmtCant(l.cantidad)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Página principal ──────────────────────────────────────────── */
export default function MovimientosPage() {
  const { user } = useAuth();
  const esAdmin = !user || user.rol?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'admin';

  const [movimientos, setMov]         = useState([]);
  const [almacenes,   setAlms]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [showModal,   setModal]       = useState(false);
  const [showCompras, setShowCompras] = useState(false);
  const [filtros,     setFiltros]     = useState({ almacen_id: '', tipo: '', desde: '', hasta: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rm, ra] = await Promise.all([
        api.get('/api/movimientos', { params: filtros }),
        api.get('/api/almacenes'),
      ]);
      setMov(rm.data?.data || []);
      setAlms(ra.data?.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="inv-page">
      <div className="inv-toolbar">
        <button className="inv-btn-nuevo" onClick={() => setModal(true)}>
          <FiPlus /> Nuevo Movimiento
        </button>
        <button className="inv-btn-compras" onClick={() => setShowCompras(true)}>
          <FiShoppingBag size={14} /> Compras
        </button>
        <select className="inv-filter-select" value={filtros.almacen_id}
          onChange={e => setFiltros(p => ({ ...p, almacen_id: e.target.value }))}>
          <option value="">Todos los almacenes</option>
          {almacenes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <select className="inv-filter-select" value={filtros.tipo}
          onChange={e => setFiltros(p => ({ ...p, tipo: e.target.value }))}>
          <option value="">Entradas y Salidas</option>
          <option value="ENTRADA">Solo Entradas</option>
          <option value="SALIDA">Solo Salidas</option>
        </select>
        <input type="date" className="inv-filter-date" value={filtros.desde}
          onChange={e => setFiltros(p => ({ ...p, desde: e.target.value }))} title="Desde" />
        <span className="inv-filter-sep">→</span>
        <input type="date" className="inv-filter-date" value={filtros.hasta}
          onChange={e => setFiltros(p => ({ ...p, hasta: e.target.value }))} title="Hasta" />
        {(filtros.almacen_id || filtros.tipo || filtros.desde || filtros.hasta) && (
          <button className="inv-btn-clear"
            onClick={() => setFiltros({ almacen_id:'', tipo:'', desde:'', hasta:'' })}>
            <FiX size={13} /> Limpiar
          </button>
        )}
      </div>

      {loading ? (
        <div className="inv-loading">Cargando movimientos...</div>
      ) : error ? (
        <div className="inv-error">{error}</div>
      ) : (
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th></th>
                <th>Almacén</th>
                <th>Fecha</th>
                <th>Número</th>
                <th>Tipo</th>
                <th>Causa</th>
                <th>Items</th>
                <th>Observación</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {movimientos.length === 0 ? (
                <tr><td colSpan={9} className="inv-empty">Sin movimientos registrados</td></tr>
              ) : movimientos.map(m => <FilaMovimiento key={m.id} mov={m} esAdmin={esAdmin} onAnular={load} />)}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ModalMovimiento
          almacenes={almacenes}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}

      {showCompras && (
        <ModalComprasPendientes
          almacenes={almacenes}
          onClose={() => setShowCompras(false)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
